import { Navigate, useLocation } from 'react-router';

import { paths, REDIRECT_TO_KEY } from '@/config/paths';
import { useAuth } from '@/features/auth/api/queries.ts';
import { useProfilesData } from '@/features/user';
import { Spinner } from '@/ui/spinner';

import { ForceSelectProfile } from './routes/app/profile/select';

const ROUTE_EXCEPTIONS = {
  ONBOARDING: [
    paths.app.onboarding.path,
    paths.app.profiles.path,
    paths.profile.select.path,
  ],
  ROOT: [paths.app.root.path],
};

export const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isSuccess } = useProfilesData();
  if (isSuccess) {
    return <Navigate to={paths.app.root.path} />;
  }
  return children;
};

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoggingIn } = useAuth();
  const { isError, isFetching, selectedProfileId, shouldShowOnboarding } =
    useProfilesData();
  const location = useLocation();

  if (isLoggingIn || isFetching) {
    return <Spinner fullPage />;
  }

  if (isError) {
    const currentPath = location.pathname + location.search;
    const loginUrl = paths.auth.login.getHref({
      queryParams: { [REDIRECT_TO_KEY]: currentPath },
    });
    return <Navigate to={loginUrl} />;
  }

  if (!selectedProfileId) {
    return <ForceSelectProfile />;
  }

  /**
   * Redirect to onboarding if -
   *  1. User is an onboarding candidate
   *  2. No batch is assigned
   *  3. No on an exception route
   */
  if (shouldShowOnboarding) {
    if (!ROUTE_EXCEPTIONS.ONBOARDING.includes(location.pathname)) {
      return <Navigate to={paths.app.onboarding.path} />;
    }
  } else {
    if (
      location.pathname.includes(paths.app.onboarding.path) &&
      !ROUTE_EXCEPTIONS.ROOT.includes(location.pathname)
    ) {
      return <Navigate to={paths.app.root.path} />;
    }
  }

  return children;
};
